import { useCallback, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useFormData } from 'brief-form'
import { ApiError } from 'commons/types'
import { isEqual } from 'lodash'
import { updateFormAfterApiError } from 'ui/components'
import { ClientUserListResponseItem, ClientUserPermission } from '~bank-client-users/types'

import { handlerServerError } from '@orbit/errors'

import { ClientUserGeneralInformationType, CreateClientUserFormType, EditClientUserFormType } from '../types'
import { useUserFormRequest } from './useUserFormRequest'

export const useCreateUserDialog = (
  clientUserForEditing?: ClientUserListResponseItem,
  permissionsStructure?: ClientUserPermission[],
  onClose?: () => void
) => {
  const queryClient = useQueryClient()

  const isModeCreating = useMemo(() => typeof clientUserForEditing === typeof undefined, [clientUserForEditing])

  const generalInformationInitialFormValue: ClientUserGeneralInformationType = {
    email: '',
    firstName: '',
    lastName: '',
    phone: ''
  }

  const [isFormChanged, setIsFormChanged] = useState(false)

  const topSidePermissions: string[] = []

  const permissionsFormValues = {}

  const trueEditPermissions: string[] = []

  clientUserForEditing?.permissions.forEach((permission) => {
    if (isModeCreating || permission.enabled) trueEditPermissions.push(permission.value)
    if (permission.nested.length) {
      permission?.nested.forEach(
        (nestedPermission) =>
          (isModeCreating || nestedPermission.enabled) && trueEditPermissions.push(nestedPermission.value)
      )
    }
  })

  permissionsStructure?.forEach((permission) => {
    permissionsFormValues[permission.value] = trueEditPermissions.includes(permission.value)
    if (permission.nested.length) {
      topSidePermissions.push(permission.value)
      permission.nested?.forEach((nestedPermission) => {
        permissionsFormValues[nestedPermission.value] = trueEditPermissions.includes(nestedPermission.value)
      })
    }
  })

  let initialFormValue = {
    assignedRelationships: isModeCreating
      ? []
      : (clientUserForEditing?.assignedRelationships.map((relationship) => relationship.id) ?? []),
    ...permissionsFormValues
  }

  if (isModeCreating) initialFormValue = { ...initialFormValue, ...generalInformationInitialFormValue }

  const onBeforeChange = ({ value, errors }) => {
    setIsFormChanged(
      Object.entries(value)
        .map((item) => !topSidePermissions.includes(item[0]) && !isEqual(item[1], initialFormValue[item[0]]))
        .filter(Boolean).length > 0
    )
    return { value, errors }
  }

  const { config, Field, validate } = useFormData<EditClientUserFormType | CreateClientUserFormType>({
    initialValue: initialFormValue,
    onBeforeChange
  })

  const handlerFormError = useCallback(
    (error: any) => {
      handlerServerError(error, { form: config })
    },
    [config]
  )

  const onCloseWrapper = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const { isSubmittingClientUserForm, createClientUser, editClientUser } = useUserFormRequest(
    onCloseWrapper,
    () => queryClient.invalidateQueries({ stale: true }),
    clientUserForEditing?.id,
    handlerFormError
  )

  const onSubmitWrapper = useCallback(() => {
    try {
      if (validate({ updateFields: true }).valid) {
        if (isModeCreating) createClientUser(config?.value as CreateClientUserFormType)
        else editClientUser(config?.value)
      }
    } catch (error: unknown) {
      updateFormAfterApiError(config.value, config.errors, error as ApiError, (v, e) => {
        config.onChange(v, e)
      })
    }
  }, [createClientUser, editClientUser, config, validate, isModeCreating])

  return {
    isModeCreating,
    onCloseWrapper,
    onSubmitWrapper,
    isFormChanged,
    config,
    Field,
    isSubmittingClientUserForm
  }
}
